import { styled, SxProp } from 'dripsy';
import React, { Fragment } from 'react';
import { View } from 'react-native';
import { ModalComponentProp } from 'react-native-modalfy';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import { BaseText } from '../common/BaseText';
import { IconTouchable } from '../common/IconTouchable';
import { Touchable } from '../common/Touchable';
import { ModalStackParamsType } from '../../modals/types';
import { Line } from '../common/Line';
import { ScrollView } from '../common/ScrollView';
import { Separator } from '../common/Separator';
import { BottomModalView } from './components/BottomModalView';

const ModalViewContainer = styled(BottomModalView)({
  px: '$6',
});

const CrossIconContainer = styled(View)({
  alignItems: 'flex-end',
  width: '100%',
  px: '$6',
});

const ScrollViewContainer = styled(ScrollView)({
  flexGrow: 0,
});

function contentContainerStyle(insets: EdgeInsets): SxProp {
  return (theme) => ({
    pb: theme.space.$4 + insets.bottom,
    pl:
      insets.left && insets.right
        ? theme.space.$6 + insets.left
        : '$6',
    pr:
      insets.left && insets.right
        ? theme.space.$6 + insets.right
        : '$6',
  });
}

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  py: '$4',
});

const Label = styled(BaseText)({
  variant: 'text.medium',
});

interface IActiveContainerProps {
  active: boolean;
}

const ActiveContainer = styled(View)(
  (props: IActiveContainerProps) => ({
    flexGrow: 1,
    pl: '$4',
    py: '$5',
    bg: props.active ? '$primary80' : null,
    borderRadius: 8,
  }),
);

type PickerItemType<T = any> = {
  title: string;
  value: T;
};

export interface IPickerModalParams {
  options: PickerItemType[];
  onSelect(item: PickerItemType): void;
  selectedValue: any;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'PickerModal'
>;

// TODO: Add PickerMenu component that will use PickerPopover and PickerModel
export function PickerModal({ modal }: ModalPropsType) {
  const insets = useSafeAreaInsets();
  const options = modal.getParam('options');
  // FIXME: when we pass `selectedValue` as null `getParam` returns `undefined`
  const selectedValue = modal.getParam(
    'selectedValue',
    modal.params?.selectedValue,
  );
  const onSelect = modal.getParam('onSelect');

  function closeModalByCrossIcon() {
    modal.closeModal();
  }

  function select(item: PickerItemType) {
    onSelect(item);
    modal.closeModal();
  }

  return (
    <ModalViewContainer>
      <Separator size="$5" />
      <CrossIconContainer>
        <IconTouchable
          onPress={closeModalByCrossIcon}
          name="close-light"
          size={24}
        />
      </CrossIconContainer>

      <Separator size="$2" />
      <ScrollViewContainer
        contentContainerSx={contentContainerStyle(insets)}
      >
        {options.map((item, index) => {
          const active = selectedValue === item.value;
          const isFistItem = index === 0;

          return (
            <Fragment key={index}>
              {!isFistItem && <Line />}
              <TouchableContainer onPress={() => select(item)}>
                <ActiveContainer active={active}>
                  <Label>{item.title}</Label>
                </ActiveContainer>
              </TouchableContainer>
            </Fragment>
          );
        })}
      </ScrollViewContainer>
    </ModalViewContainer>
  );
}
